import React from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import './WingsPagination.css'

const elementInstanceType =
  typeof Element !== 'undefined' ? PropTypes.instanceOf(Element) : PropTypes.any

/**
 * Build a continuous sliding window of page numbers centered on currentPage.
 * Matches the default Algolia Pagination behaviour: no ellipsis, no anchored
 * first/last entries — just a plain consecutive range clamped to [1, totalPages].
 *
 * @param {number} currentPage - 1-indexed current page
 * @param {number} totalPages  - total number of pages
 * @param {number} padding     - number of pages to show on each side of current (default 3)
 * @returns {number[]}
 */
const buildPageWindow = (currentPage, totalPages, padding = 3) => {
  const windowSize = (padding * 2) + 1

  // Keep a consistent window size when possible, anchoring to page 1 near the start
  // and to the last page near the end.
  let start = Math.max(1, currentPage - padding)
  let end = Math.min(totalPages, start + windowSize - 1)
  start = Math.max(1, end - windowSize + 1)

  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

/**
 * WingsPagination Component
 *
 * A reusable, purely-presentational pagination control styled to match the
 * explore-page Algolia pagination. All state is owned by the parent.
 *
 * @component
 * @example
 * <WingsPagination
 *   currentPage={2}
 *   totalPages={8}
 *   onPageChange={(page) => setPage(page)}
 * />
 *
 * @param {Object}   props
 * @param {number}   props.currentPage   - Active page number, 1-indexed.
 * @param {number}   props.totalPages    - Total number of available pages.
 * @param {Function} props.onPageChange  - Callback invoked with the selected page number (1-indexed).
 * @param {string}   [props.className]   - Additional CSS classes to merge onto the root element.
 * @param {React.RefObject} [props.scrollRef] - Optional ref to an element; when provided, scrolls it into view on every page change.
 */
const WingsPagination = ({
  currentPage,
  totalPages,
  onPageChange,
  className = '',
  scrollRef = null,
}) => {
  if (totalPages <= 1) return null

  const handlePageChange = (page) => {
    onPageChange(page)
    if (scrollRef?.current) {
      scrollRef.current.style.scrollMarginTop = "calc(var(--w-header-height) + var(--w-spacing-1))"
      scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const pageWindow = buildPageWindow(currentPage, totalPages)
  const isPrevDisabled = currentPage <= 1
  const isNextDisabled = currentPage >= totalPages

  return (
    <nav
      className={['wings-pagination', className].filter(Boolean).join(' ')}
      aria-label="Pagination"
    >
      <ul className="wings-pagination-list">

        {/* Previous */}
        <li
          className={[
            'wings-pagination-item',
            'wings-pagination-item-prev',
            isPrevDisabled ? 'wings-pagination-item--disabled' : '',
          ].filter(Boolean).join(' ')}
        >
          <button
            className="wings-pagination-link"
            onClick={() => !isPrevDisabled && handlePageChange(currentPage - 1)}
            disabled={isPrevDisabled}
            aria-label="Previous page"
          >
            <FontAwesomeIcon icon={faChevronLeft} size="sm" />
          </button>
        </li>

        {/* Page numbers — continuous sliding window, no ellipsis */}
        {pageWindow.map((page) => (
          <li
            key={page}
            className={[
              'wings-pagination-item',
              page === currentPage ? 'wings-pagination-item--selected' : '',
            ].filter(Boolean).join(' ')}
          >
            <button
              className={[
                  'wings-pagination-link',
                page === currentPage ? 'wings-pagination-link--selected' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => page !== currentPage && handlePageChange(page)}
              aria-label={`Page ${page}`}
              aria-current={page === currentPage ? 'page' : undefined}
            >
              {page}
            </button>
          </li>
        ))}

        {/* Next */}
        <li
          className={[
            'wings-pagination-item',
            'wings-pagination-item-next',
            isNextDisabled ? 'wings-pagination-item--disabled' : '',
          ].filter(Boolean).join(' ')}
        >
          <button
            className="wings-pagination-link"
            onClick={() => !isNextDisabled && handlePageChange(currentPage + 1)}
            disabled={isNextDisabled}
            aria-label="Next page"
          >
            <FontAwesomeIcon icon={faChevronRight} size="sm" />
          </button>
        </li>

      </ul>
    </nav>
  )
}

WingsPagination.propTypes = {
  /** Active page number, 1-indexed. */
  currentPage: PropTypes.number.isRequired,
  /** Total number of available pages. */
  totalPages: PropTypes.number.isRequired,
  /** Callback invoked with the new page number (1-indexed) when the user navigates. */
  onPageChange: PropTypes.func.isRequired,
  /** Additional CSS classes to apply to the root nav element. */
  className: PropTypes.string,
  /** Optional ref — when provided, scrolls the element into view on every page change. */
  scrollRef: PropTypes.shape({ current: elementInstanceType }),
}

export default WingsPagination
